/**
 * Metadata for currencies the picker knows how to render — flags, display
 * symbols, and default decimals. The upstream FX API returns 160+ rates;
 * we surface any currency with a meta entry AND a live rate.
 *
 * Symbol convention: we always render "SYMBOL amount" (with a space) so
 * mixed 3-letter (AED, INR) and glyph ($, €) codes align. Decimals follow
 * ISO 4217 minor-unit conventions.
 */
export const CURRENCY_META = {
  // Middle East / Gulf
  AED: { flag: "🇦🇪", symbol: "AED", name: "UAE Dirham",         decimals: 2 },
  SAR: { flag: "🇸🇦", symbol: "SAR", name: "Saudi Riyal",        decimals: 2 },
  QAR: { flag: "🇶🇦", symbol: "QAR", name: "Qatari Riyal",       decimals: 2 },
  KWD: { flag: "🇰🇼", symbol: "KWD", name: "Kuwaiti Dinar",      decimals: 3 },
  BHD: { flag: "🇧🇭", symbol: "BHD", name: "Bahraini Dinar",     decimals: 3 },
  OMR: { flag: "🇴🇲", symbol: "OMR", name: "Omani Rial",         decimals: 3 },
  JOD: { flag: "🇯🇴", symbol: "JOD", name: "Jordanian Dinar",    decimals: 3 },
  LBP: { flag: "🇱🇧", symbol: "LBP", name: "Lebanese Pound",     decimals: 0 },
  EGP: { flag: "🇪🇬", symbol: "EGP", name: "Egyptian Pound",     decimals: 2 },
  ILS: { flag: "🇮🇱", symbol: "₪",   name: "Israeli Shekel",     decimals: 2 },
  TRY: { flag: "🇹🇷", symbol: "₺",   name: "Turkish Lira",       decimals: 2 },
  IRR: { flag: "🇮🇷", symbol: "IRR", name: "Iranian Rial",       decimals: 0 },
  PKR: { flag: "🇵🇰", symbol: "PKR", name: "Pakistani Rupee",    decimals: 2 },

  // Major reserve currencies
  USD: { flag: "🇺🇸", symbol: "$",   name: "US Dollar",          decimals: 2 },
  EUR: { flag: "🇪🇺", symbol: "€",   name: "Euro",               decimals: 2 },
  GBP: { flag: "🇬🇧", symbol: "£",   name: "British Pound",      decimals: 2 },
  CHF: { flag: "🇨🇭", symbol: "CHF", name: "Swiss Franc",        decimals: 2 },
  JPY: { flag: "🇯🇵", symbol: "¥",   name: "Japanese Yen",       decimals: 0 },
  CNY: { flag: "🇨🇳", symbol: "¥",   name: "Chinese Yuan",       decimals: 2 },
  HKD: { flag: "🇭🇰", symbol: "HK$", name: "Hong Kong Dollar",   decimals: 2 },

  // Asia-Pacific
  AUD: { flag: "🇦🇺", symbol: "A$",  name: "Australian Dollar",  decimals: 2 },
  NZD: { flag: "🇳🇿", symbol: "NZ$", name: "New Zealand Dollar", decimals: 2 },
  SGD: { flag: "🇸🇬", symbol: "S$",  name: "Singapore Dollar",   decimals: 2 },
  INR: { flag: "🇮🇳", symbol: "₹",   name: "Indian Rupee",       decimals: 2 },
  KRW: { flag: "🇰🇷", symbol: "₩",   name: "Korean Won",         decimals: 0 },
  MYR: { flag: "🇲🇾", symbol: "RM",  name: "Malaysian Ringgit",  decimals: 2 },
  THB: { flag: "🇹🇭", symbol: "฿",   name: "Thai Baht",          decimals: 2 },
  IDR: { flag: "🇮🇩", symbol: "Rp",  name: "Indonesian Rupiah",  decimals: 0 },
  PHP: { flag: "🇵🇭", symbol: "₱",   name: "Philippine Peso",    decimals: 2 },
  VND: { flag: "🇻🇳", symbol: "₫",   name: "Vietnamese Dong",    decimals: 0 },
  TWD: { flag: "🇹🇼", symbol: "NT$", name: "Taiwan Dollar",      decimals: 2 },
  BDT: { flag: "🇧🇩", symbol: "৳",   name: "Bangladeshi Taka",   decimals: 2 },
  LKR: { flag: "🇱🇰", symbol: "Rs",  name: "Sri Lankan Rupee",   decimals: 2 },

  // Americas
  CAD: { flag: "🇨🇦", symbol: "C$",  name: "Canadian Dollar",    decimals: 2 },
  MXN: { flag: "🇲🇽", symbol: "MX$", name: "Mexican Peso",       decimals: 2 },
  BRL: { flag: "🇧🇷", symbol: "R$",  name: "Brazilian Real",     decimals: 2 },
  ARS: { flag: "🇦🇷", symbol: "ARS", name: "Argentine Peso",     decimals: 2 },
  CLP: { flag: "🇨🇱", symbol: "CLP", name: "Chilean Peso",       decimals: 0 },
  COP: { flag: "🇨🇴", symbol: "COP", name: "Colombian Peso",     decimals: 2 },
  PEN: { flag: "🇵🇪", symbol: "S/",  name: "Peruvian Sol",       decimals: 2 },

  // Europe (non-Euro)
  SEK: { flag: "🇸🇪", symbol: "kr",  name: "Swedish Krona",      decimals: 2 },
  NOK: { flag: "🇳🇴", symbol: "kr",  name: "Norwegian Krone",    decimals: 2 },
  DKK: { flag: "🇩🇰", symbol: "kr",  name: "Danish Krone",       decimals: 2 },
  PLN: { flag: "🇵🇱", symbol: "zł",  name: "Polish Złoty",       decimals: 2 },
  CZK: { flag: "🇨🇿", symbol: "Kč",  name: "Czech Koruna",       decimals: 2 },
  HUF: { flag: "🇭🇺", symbol: "Ft",  name: "Hungarian Forint",   decimals: 0 },
  RON: { flag: "🇷🇴", symbol: "lei", name: "Romanian Leu",       decimals: 2 },
  BGN: { flag: "🇧🇬", symbol: "лв",  name: "Bulgarian Lev",      decimals: 2 },
  UAH: { flag: "🇺🇦", symbol: "₴",   name: "Ukrainian Hryvnia",  decimals: 2 },
  RUB: { flag: "🇷🇺", symbol: "₽",   name: "Russian Ruble",      decimals: 2 },
  ISK: { flag: "🇮🇸", symbol: "kr",  name: "Icelandic Króna",    decimals: 0 },

  // Africa
  ZAR: { flag: "🇿🇦", symbol: "R",   name: "South African Rand", decimals: 2 },
  NGN: { flag: "🇳🇬", symbol: "₦",   name: "Nigerian Naira",     decimals: 2 },
  KES: { flag: "🇰🇪", symbol: "KSh", name: "Kenyan Shilling",    decimals: 2 },
  GHS: { flag: "🇬🇭", symbol: "₵",   name: "Ghanaian Cedi",      decimals: 2 },
  MAD: { flag: "🇲🇦", symbol: "DH",  name: "Moroccan Dirham",    decimals: 2 },
  TND: { flag: "🇹🇳", symbol: "DT",  name: "Tunisian Dinar",     decimals: 3 },
  DZD: { flag: "🇩🇿", symbol: "DA",  name: "Algerian Dinar",     decimals: 2 },
};
